import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../components/layout";
import { Table, Card, Row, Col, Statistic, Button, Modal, Calendar, Badge, Tag } from "antd";
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UnorderedListOutlined
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { useNavigate } from "react-router-dom";
import moment from "moment";

function DoctorHome() {
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState([]);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
   const [sortedInfo, setSortedInfo] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch both table rows and card numbers in parallel
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(showLoading());

        const token = "Bearer " + localStorage.getItem("token");
        const [apptRes, statsRes, calendarRes] = await Promise.all([
          axios.get("/api/doctor/get-appointments-by-doctor-id", { 
            headers: { Authorization: token } 
          }),
          axios.get("/api/doctor/dashboard-stats", { 
            headers: { Authorization: token } 
          }),
          axios.get("/api/doctor/get-calendar-appointments", { 
            headers: { Authorization: token } 
          })
        ]);

        if (apptRes.data.success) setAppointments(apptRes.data.data);
        if (statsRes.data.success) setStats(buildStats(statsRes.data.data));
        if (calendarRes.data.success) setCalendarData(calendarRes.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        dispatch(hideLoading());
      }
    };

    fetchData();
  }, [dispatch]);

  // Convert raw numbers into objects the <Statistic> cards need
  const buildStats = (raw) => ([
    {
      title: "Total Appointments",
      value: raw.totalAppointments,
      color: "#3f8600",
      icon: <ArrowUpOutlined />,
    },
    {
      title: "Pending Approvals",
      value: raw.pendingApprovals,
      color: raw.pendingApprovals ? "#cf1322" : "#3f8600",
      icon: raw.pendingApprovals ? <ArrowUpOutlined/> : <ArrowDownOutlined/> ,
    },
    {
      title: "Approved Appointments",
      value: raw.approvedAppointments,
      color: "#3f8600",
      icon: <ArrowUpOutlined />,
    },
    {
      title: "Completed Appointments",
      value: raw.completedAppointments,
      color: "#1890ff",
      icon: <CheckCircleOutlined />,
    },
    {
      title: "Follow-up Appointments",
      value: raw.followupAppointments,
      color: "#722ed1",
      icon: <ClockCircleOutlined />,
    },
    {
      title: "Rejected Appointments",
      value: raw.totalRejected,
      color: "#cf1322",
      icon: <CloseCircleOutlined />,
    }
  ]);

  // Calendar date cell rendering with appointments
  const dateCellRender = (value) => {
    const listData = calendarData.filter(item => item.date === value.format('YYYY-MM-DD'));
    return (
      <ul className="events">
        {listData.map(item => (
          <li key={item._id}>
            <Badge 
              status={getStatusType(item.status)} 
              text={`${item.time} - ${item.userInfo?.name || 'Patient'}`} 
            />
          </li>
        ))}
      </ul>
    );
  };

  // Get badge status based on appointment status
  const getStatusType = (status) => {
    switch(status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'completed': return 'processing';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Get tag color based on appointment status
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'blue';
      case 'pending': return 'orange';
      case 'completed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  // Helper function to get the display date
 // const getDisplayDate = (appointment) => {
 //   return appointment.schedule_date 
 //     ? new Date(appointment.schedule_date).toLocaleDateString()
 //     : new Date(appointment.date).toLocaleDateString();
 // };

  // Navigate to appointments page
  const goToAppointmentsPage = () => {
    navigate("/doctor/appointments");
  };

  return (
    <Layout>
      {/* Header with Calendar Button */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h2>Doctor Dashboard</h2>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<CalendarOutlined />} 
            onClick={() => setIsCalendarVisible(true)}
            style={{ marginRight: 10 }}
          >
            View Calendar
          </Button>
          <Button 
            type="default" 
            icon={<UnorderedListOutlined />} 
            onClick={goToAppointmentsPage}
          >
            All Appointments
          </Button>
        </Col>
      </Row>

      {/* Statistic Cards */}
      <div style={{ paddingBottom: 30 }}>
        <Row gutter={[16, 16]} justify="start">
          {stats.map((stat, idx) => (
            <Col key={idx} xs={24} sm={12} md={8} lg={6} xl={4}>
              <Card
                style={{
                  borderRadius: 12,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transition: "all .3s ease",
                }}
                bodyStyle={{ padding: 20 }}
                hoverable
              >
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  precision={0}
                  valueStyle={{ color: stat.color, fontWeight: "bold" }}
                  prefix={stat.icon}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Appointment Table */}
      <Card 
        title={
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            Recent Patient Appointments
          </span>
        }
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
        extra={
          <Button 
            type="link" 
            icon={<UnorderedListOutlined />}
            onClick={goToAppointmentsPage}
          >
            View All
          </Button>
        }
      >
        <Table
          dataSource={appointments.slice(0, 5)} // Show only 5 recent appointments
            onChange={(_, s) => {
          setSortedInfo(s);
        }}
          columns={[
            {
              title: "Patient",
              key: "patient",
              render: (_, record) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{record.userInfo?.name || "N/A"}</div>
                  <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                    {record.userInfo?.phone || "No contact"}
                  </div>
                </div>
              ),
            },

{
  title: "Booked Date",
  key: "date",
  render: (_, record) =>
    record.date ? moment(record.date).format("Do MMMM YYYY") : "-"
    // Example: "4th September 2025"
},
{
  title: "Booked Time",
  key: "time",
  render: (_, record) =>
    record.time ? moment(record.time, "HH:mm:ss").format("hh:mm A") : "-"
    // Example: "02:30 PM"
            },

              {
                  title: "Scheduled Date",
                  key: "schedule_date",
                  render: (_, r) =>
                    r.schedule_date
                      ? moment(r.schedule_date).format("DD-MM-YYYY")
                      : "Not set",
                  sorter: (a, b) =>
                    moment(a.schedule_date || 0).unix() -
                    moment(b.schedule_date || 0).unix(),
                  sortOrder:
                    sortedInfo.columnKey === "schedule_date"
                      ? sortedInfo.order
                      : null,
                },
            { 
              title: "Appointment Status", 
              dataIndex: "status", 
              key: "status",
              render: (status) => (
                <Tag color={getStatusColor(status)}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Tag>
              )
            }
          ]}
          rowKey="_id"
          pagination={false}
        />
      </Card>

      {/* Calendar Modal */}
      <Modal
        title="Appointment Calendar"
        open={isCalendarVisible}
        onCancel={() => setIsCalendarVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsCalendarVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <Calendar 
          cellRender={dateCellRender} 
          mode="month"
        />
        
        <div style={{ marginTop: 16 }}>
          <h4>Status Legend:</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <span><Badge status="success" text="Approved" /></span>
            <span><Badge status="warning" text="Pending" /></span>
            <span><Badge status="processing" text="Completed" /></span>
            <span><Badge status="error" text="Rejected" /></span>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}

export default DoctorHome;